import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  ProcedureActionItem,
  ProcedureSubProcedureItem,
} from 'projects/shared/src/lib/core/interfaces';
import {
  proceduresFeatureKey,
  ProceduresState,
  ProcedureItemsState,
} from './procedures.reducer';

const APPROVED_SUB_PROCEDURE_STATUSES = new Set(['Use', 'Approved']);
const APPROVED_ACTION_STATUSES = new Set(['Use', 'Approved']);

function isSubProcedureApproved(item: ProcedureSubProcedureItem): boolean {
  return APPROVED_SUB_PROCEDURE_STATUSES.has(item.currentStatus);
}

function isSubProcedureLocked(item: ProcedureSubProcedureItem): boolean {
  return item.locked && !item.skippable;
}

function isActionApproved(item: ProcedureActionItem): boolean {
  return APPROVED_ACTION_STATUSES.has(item.currentStatus);
}

function isActionLocked(item: ProcedureActionItem): boolean {
  return item.locked && !item.skippable;
}

export const selectProceduresState =
  createFeatureSelector<ProceduresState>(proceduresFeatureKey);

export const selectProceduresList = createSelector(
  selectProceduresState,
  (state) =>
    state.procedures.map((procedure) => ({
      ...procedure,
      lockTableRow: !procedure.isRelevant,
    })),
);

export const selectProceduresIsLoading = createSelector(
  selectProceduresState,
  (state) => state.isLoading,
);

export const selectProcedureDetailsLoaded = createSelector(
  selectProceduresState,
  (state) => state.procedureDetailsLoaded,
);

// Procedure Items Selectors
export const selectProcedureItemsState = createSelector(
  selectProceduresState,
  (state) => state.procedureItems,
);

export const selectProcedureHeader = createSelector(
  selectProcedureItemsState,
  (state: ProcedureItemsState) => state.header,
);

export const selectProcedureItems = createSelector(
  selectProcedureItemsState,
  (state: ProcedureItemsState) => state.items,
);

export const selectProcedureItemsCanChangeStatus = createSelector(
  selectProcedureItems,
  (items) => items?.canChangeStatus ?? false,
);

export const selectApproveAllSubProceduresDisabled = createSelector(
  selectProcedureItems,
  (items) => items?.approveAllSubProceduresDisabled ?? false,
);

export const selectApproveAllActionsDisabled = createSelector(
  selectProcedureItems,
  (items) => items?.approveAllActionsDisabled ?? false,
);

export const selectProcedureSubProcedures = createSelector(
  selectProcedureItems,
  (items) => items?.subProcedures ?? [],
);

export const selectAllSubProceduresApproved = createSelector(
  selectProcedureSubProcedures,
  (subProcedures) =>
    subProcedures.length > 0 && subProcedures.every(isSubProcedureApproved),
);

export const selectAllSubProceduresLocked = createSelector(
  selectProcedureSubProcedures,
  (subProcedures) =>
    subProcedures.length > 0 && subProcedures.every(isSubProcedureLocked),
);

export const selectProcedureActionsByPolicy = createSelector(
  selectProcedureItems,
  (items) => {
    return items?.actionsByPolicy ?? [];
  },
);

export const selectAllActionsApproved = createSelector(
  selectProcedureActionsByPolicy,
  (actionsByPolicy) =>
    actionsByPolicy.length > 0 &&
    actionsByPolicy
      .flatMap((group) => group.controls)
      .flatMap((control) => control.actions)
      .every(isActionApproved),
);

export const selectAllActionsLocked = createSelector(
  selectProcedureActionsByPolicy,
  (actionsByPolicy) => {
    const actions = actionsByPolicy
      .flatMap((group) => group.controls)
      .flatMap((control) => control.actions);

    return actions.length > 0 && actions.every(isActionLocked);
  },
);

export const selectAllActionsApprovedByPolicyId = createSelector(
  selectProcedureActionsByPolicy,
  (actionsByPolicy) =>
    actionsByPolicy.reduce(
      (acc, group) => {
        acc[group.policyId] = group.controls
          .flatMap((control) => control.actions)
          .every(isActionApproved);
        return acc;
      },
      {} as Record<string, boolean>,
    ),
);

export const selectAllActionsLockedByPolicyId = createSelector(
  selectProcedureActionsByPolicy,
  (actionsByPolicy) =>
    actionsByPolicy.reduce(
      (acc, group) => {
        const actions = group.controls.flatMap((control) => control.actions);
        acc[group.policyId] =
          actions.length > 0 && actions.every(isActionLocked);
        return acc;
      },
      {} as Record<string, boolean>,
    ),
);

export const selectProcedureItemsIsLoadingItems = createSelector(
  selectProcedureItemsState,
  (state: ProcedureItemsState) => state.isLoadingItems,
);

export const selectProcedureItemsIsRefreshing = createSelector(
  selectProcedureItemsState,
  (state: ProcedureItemsState) => state.isRefreshingItems,
);

export const selectProcedureItemsIsUpdating = createSelector(
  selectProcedureItemsState,
  (state: ProcedureItemsState) => state.isUpdatingItem,
);

export const selectIsApprovingAllSubProcedures = createSelector(
  selectProcedureItemsState,
  (state: ProcedureItemsState) => state.isApprovingAllSubProcedures,
);

export const selectIsApprovingAllActions = createSelector(
  selectProcedureItemsState,
  (state: ProcedureItemsState) => state.isApprovingAllActions,
);

export const selectApprovingPolicyIds = createSelector(
  selectProcedureItemsState,
  (state: ProcedureItemsState) => state.approvingPolicyIds,
);

export const selectProcedureItemsApprovedCount = createSelector(
  selectProcedureItemsState,
  (state: ProcedureItemsState) => state.itemsApprovedCount,
);

// Sidebar Selectors
export const selectProcedureSidebarState = createSelector(
  selectProceduresState,
  (state) => state.sidebar,
);

export const selectProcedureSidebarIsOpen = createSelector(
  selectProcedureSidebarState,
  (sidebar) => sidebar.isOpen,
);

export const selectProcedureDetails = createSelector(
  selectProcedureSidebarState,
  (sidebar) => sidebar.procedureDetails,
);

export const selectOppenedProcedure = createSelector(
  selectProceduresState,
  (state: ProceduresState) => {
    return state.procedures.find(
      (procedure) => procedure.procedureId === state.openedProcedureId,
    );
  },
);

export const selectProcedureSidebarIsLoading = createSelector(
  selectProcedureSidebarState,
  (sidebar) => sidebar.isLoading,
);

export const selectProcedureCommentPending = createSelector(
  selectProcedureSidebarState,
  (sidebar) => sidebar.commentPending,
);

export const selectProcedureCommentFailed = createSelector(
  selectProcedureSidebarState,
  (sidebar) => sidebar.commentFailed,
);

export const selectProcedureNewComment = createSelector(
  selectProcedureSidebarState,
  (sidebar) => sidebar.newComment,
);

// Action Item Sidebar Selectors
export const selectActionItemSidebarState = createSelector(
  selectProceduresState,
  (state) => state.actionItemSidebar,
);

export const selectActionItemSidebarIsOpen = createSelector(
  selectActionItemSidebarState,
  (sidebar) => sidebar.isOpen,
);

export const selectActionDetails = createSelector(
  selectActionItemSidebarState,
  (sidebar) => sidebar.actionDetails,
);

export const selectActionItemSidebarIsLoading = createSelector(
  selectActionItemSidebarState,
  (sidebar) => sidebar.isLoading,
);

export const selectActionItemCommentPending = createSelector(
  selectActionItemSidebarState,
  (sidebar) => sidebar.commentPending,
);

export const selectActionItemCommentFailed = createSelector(
  selectActionItemSidebarState,
  (sidebar) => sidebar.commentFailed,
);

export const selectActionItemNewComment = createSelector(
  selectActionItemSidebarState,
  (sidebar) => sidebar.newComment,
);

export const selectProcedureStatuses = createSelector(
  selectProceduresState,
  (state) => state.statuses,
);

export const selectProcedureItemStatuses = createSelector(
  selectProceduresState,
  (state) => state.itemStatuses ?? [],
);

export const selectSelectedProcedureItem = createSelector(
  selectProcedureItemsState,
  (state: ProcedureItemsState) => state.selectedProcedureItem,
);
